import { HttpClient, HttpHeaders } from '@angular/common/http';
import { InjectionToken } from '@angular/core';

import { retryWhen } from 'rxjs/operators';

import { HttpRequestHeadersService } from './http-request-headers.service';
import { HttpRequestParamsService } from './http-request-params.service';
import { HttpRequestRetryPolicyService } from './http-request-retry-policy.service';

import { RequestOptions, RetryPolicy } from 'app/core/models';

/**
 * Service to perform Http request.
 * @export
 */
export class HttpRequestService {
    /** @property apiEndpoint - base url of all http requests that will be performed by this service
     */
    apiEndpoint: string;

    /** @property headersService - service injected responsible for defining all headers that will be sent on each request
     */
    headersService: HttpRequestHeadersService;

    /** @property paramsService - service injected responsible for defining all query string parameters that will be sent on each request
     */
    paramsService: HttpRequestParamsService;

    /** @property retryPolicyService - service injected responsible for defining desired retry policy on failure (unsuccessful http requests)
     */
    retryPolicyService: HttpRequestRetryPolicyService;

    /**
     * Creates an instance of HttpRequestService.
     * @param http - Angular HttpClient module that allows us to perform http requests
     * @param headersService - service injected responsible for defining all headers that will be sent on each request
     * @param paramsService - service injected responsible for defining all query string parameters that will be sent on each request
     * @param retryPolicyService - service injected responsible for defining desired retry policy on failure (unsuccessful http requests)
     */
    constructor(
        private http: HttpClient,
        headersService: HttpRequestHeadersService,
        paramsService: HttpRequestParamsService,
        retryPolicyService: HttpRequestRetryPolicyService,
        apiEndpoint: string
    ) {
        this.apiEndpoint = apiEndpoint;
        this.headersService = headersService;
        this.paramsService = paramsService;
        this.retryPolicyService = retryPolicyService;
    }

    /**
     * Get current Tenant.
     */
    getCurrentTenant(): string {
        return this.headersService.tenant;
    }

    /**
     * Perform GET requests.
     */
    async get<T>(partialUrl: string, customRetryPolicy?: RetryPolicy, customRequestOptions?: RequestOptions): Promise<T> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions();
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);
        const result = await this.http.get<T>(url, opt).pipe(retryWhen(retryPolicy)).toPromise();
        return result;
    }

    /**
     * Perform GET requests with 'Blob' responseType - binary content.
     */
    async getBlob(partialUrl: string, customRetryPolicy?: RetryPolicy, customRequestOptions?: RequestOptions): Promise<Blob> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions(false);
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);

        const result = await this.http
            .get(url, { ...opt, ...{ responseType: 'blob' } })
            .pipe(retryWhen(retryPolicy))
            .toPromise();

        return result;
    }

    /**
     * Perform POST requests.
     */
    async post<T>(
        partialUrl: string,
        value: any,
        customRetryPolicy?: RetryPolicy,
        customRequestOptions?: RequestOptions,
        setContentType?: boolean
    ): Promise<T> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions(setContentType);
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);
        return await this.http.post<T>(url, value, opt).pipe(retryWhen(retryPolicy)).toPromise();
    }

    /**
     * Perform PUT requests.
     */
    async put<T>(partialUrl: string, value: any, customRetryPolicy?: RetryPolicy, customRequestOptions?: RequestOptions): Promise<T> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions();
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);
        return await this.http.put<T>(url, value, opt).pipe(retryWhen(retryPolicy)).toPromise();
    }

    /**
     * Perform PATCH requests.
     */
    async patch<T>(partialUrl: string, value: any, customRetryPolicy?: RetryPolicy, customRequestOptions?: RequestOptions): Promise<T> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions();
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);
        return await this.http.patch<T>(url, value, opt).pipe(retryWhen(retryPolicy)).toPromise();
    }

    /**
     * Perform DELETE requests.
     */
    async delete(partialUrl: string, customRetryPolicy?: RetryPolicy, customRequestOptions?: RequestOptions): Promise<any> {
        const url = this.buildUrl(this.apiEndpoint, partialUrl);
        const opt = customRequestOptions ? customRequestOptions : await this.requestOptions();
        const retryPolicy = customRetryPolicy ? customRetryPolicy : this.requestRetryPolicy();
        this.logRequest(url, opt);
        const result = await this.http.delete(url, opt).pipe(retryWhen(retryPolicy)).toPromise();
        return result;
    }

    private buildUrl(...urlParts: string[]): string {
        // sanitize each url part
        urlParts = urlParts.map((part) => {
            // verify if the string exists
            if (!part) {
                return null;
            }

            // remove leading and trailing whitespace
            // and verify if it is not empty
            part = part.trim();
            if (part === '') {
                return null;
            }

            // verify if the first char is not a slash
            if (part[0] === '/') {
                part = part.substr(1, part.length - 1);
            }

            // verify if the last char is not a slash
            if (part[part.length - 1] === '/') {
                part = part.substr(0, part.length - 1);
            }

            return part;
        });

        // remove null elements
        urlParts = urlParts.filter((part) => part !== null);

        // joint all the components
        return urlParts.join('/');
    }

    private async requestOptions(setContentType: boolean = true): Promise<RequestOptions> {
        await this.headersService.setAuthorizationHeaders(setContentType);

        return { ...this.headersService.headers, ...this.paramsService.params };
    }

    private requestRetryPolicy(): RetryPolicy {
        return this.retryPolicyService.retryPolicy;
    }

    private logRequest(url: string, opt: { headers?: HttpHeaders }): void {
        if ('indexedDB' in window) {
            let db;
            const logMessage = `Requesting ${url} with headers ${JSON.stringify(opt.headers)}`;

            const request = window.indexedDB.open('logs-http', 1);

            request.onsuccess = (_) => {
                db = request.result;
                db.transaction(['logs'], 'readwrite')
                    .objectStore('logs')
                    .add({ date: JSON.stringify(Date()), message: logMessage });
            };

            request.onupgradeneeded = (_) => {
                db = request.result;
                db.createObjectStore('logs', { keyPath: 'date' });
            };
        }
    }
}

export const HTTP_REQUEST_SERVICE_TOKEN = new InjectionToken<string>('request service token');

/**
 * Factory to provide the [HttpRequestService]{@link HttpRequestService}
 */
export function PROVIDE_HTTP_REQUEST_SERVICE_WITH_API_ENDPOINT_FACTORY(
    http: HttpClient,
    headersService: HttpRequestHeadersService,
    paramsService: HttpRequestParamsService,
    retryPolicyService: HttpRequestRetryPolicyService,
    apiEndpoint: string
): HttpRequestService {
    return new HttpRequestService(http, headersService, paramsService, retryPolicyService, apiEndpoint);
}
